import { useEffect, useState } from 'react';

import { cancelDeposit, getDepositStatus } from 'api';
import {
  DEPOSIT_OUTCOME,
  DEPOSIT_POLL,
  DEPOSIT_STATUS_OUTCOME,
  forgetDepositToken,
} from 'components/request-work-modal/deposit';

// A look that failed for a reason that may pass is asked again; one the server refused is not.
const isRetryable = (status) => !(status >= 400 && status < 500) || status === 408 || status === 429;

// The portal's status and cancel answer a signed-in customer: 401/403 says the session is gone (it can lapse while the
// customer pays), not that the server judged the token. The draft is then the customer's still, and the token stays for
// a later try.
const isSessionRefusal = (status) => status === 401 || status === 403;

const firstOutcome = (kind) => (kind === 'cancel' ? DEPOSIT_OUTCOME.checking : DEPOSIT_OUTCOME.processing);

/**
 * Where a deposit stands after the customer came back from the payment page. Paid is not known to the page the moment
 * they return: the payment is recorded when Stripe's word reaches the server, so it asks until the server has an
 * answer, and says so honestly if that takes longer than a minute. The token kept for the payment is dropped once the
 * server has answered for it, and not before: a customer whose question never got through is still holding the draft.
 */
const useDepositOutcome = ({ kind, token, portalId }) => {
  const [outcome, setOutcome] = useState(() => firstOutcome(kind));

  useEffect(() => {
    let isActive = true;
    let timer;
    let current = firstOutcome(kind);
    let isLookingAgain = false;

    const settle = (next) => {
      if (!isActive) return;

      current = next;
      setOutcome(next);
    };

    // The server has had its say about this payment (or refused to), so the token has done its work.
    const answered = (next) => {
      forgetDepositToken(portalId, token);

      settle(next);
    };

    // `waited` is the time spent so far, `delay` the wait that came before this look.
    const poll = async (waited = 0, delay = DEPOSIT_POLL.intervalMs) => {
      let nextDelay = waited >= DEPOSIT_POLL.slowAfterMs ? DEPOSIT_POLL.slowIntervalMs : DEPOSIT_POLL.intervalMs;

      try {
        const { data } = await getDepositStatus(portalId, token);
        const answer = DEPOSIT_STATUS_OUTCOME[data?.status];

        if (answer !== DEPOSIT_OUTCOME.processing) {
          // A word the page does not know is no verdict on the draft, so the token stays for a later look.
          if (answer) answered(answer);
          else settle(DEPOSIT_OUTCOME.unconfirmed);

          return;
        }
      } catch (error) {
        const status = error?.response?.status;

        if (!isRetryable(status)) {
          if (isSessionRefusal(status)) settle(DEPOSIT_OUTCOME.unconfirmed);
          else answered(DEPOSIT_OUTCOME.unconfirmed);

          return;
        }

        // Any other look that failed is no verdict: ask again, slower if the server asked to be left alone.
        if (status === 429) nextDelay = Math.min(delay * 2, DEPOSIT_POLL.maxIntervalMs);
      }

      if (!isActive) return;

      if (waited + nextDelay >= DEPOSIT_POLL.totalMs) {
        settle(DEPOSIT_OUTCOME.confirming);

        return;
      }

      timer = setTimeout(() => poll(waited + nextDelay, nextDelay), nextDelay);
    };

    const pause = (ms) => new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    });

    // A cancel that did not get through is asked again a couple of times: giving up on it would leave the draft Pending
    // and holding the slot, with a page that says nothing true about the request.
    const cancel = async () => {
      for (let attempt = 1; attempt <= DEPOSIT_POLL.cancelTries; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await cancelDeposit(portalId, token);
          const answer = DEPOSIT_STATUS_OUTCOME[data?.status];

          // The payment is still going through after all: from here it is watched like any other.
          if (answer === DEPOSIT_OUTCOME.processing) {
            if (!isActive) return;

            settle(DEPOSIT_OUTCOME.processing);
            poll(0);

            return;
          }

          if (answer) answered(answer);
          else settle(DEPOSIT_OUTCOME.unconfirmed);

          return;
        } catch (error) {
          const refusedWith = error?.response?.status;

          if (!isRetryable(refusedWith)) {
            // Refused: the server did answer, and there is no draft of this token to hold anything. Unless it was the
            // session that was refused: nothing is known of the draft then, and the token stays.
            if (isSessionRefusal(refusedWith)) settle(DEPOSIT_OUTCOME.unconfirmed);
            else answered(DEPOSIT_OUTCOME.unconfirmed);

            return;
          }
        }

        if (attempt < DEPOSIT_POLL.cancelTries) {
          // eslint-disable-next-line no-await-in-loop
          await pause(DEPOSIT_POLL.cancelPauseMs);

          if (!isActive) return;
        }
      }

      // Never answered: what became of the draft is not known, and the token stays so that a new try can find it.
      settle(DEPOSIT_OUTCOME.unconfirmed);
    };

    // The page stopped watching on its own; a customer back on the tab gets one more look.
    const onVisible = async () => {
      if (document.visibilityState !== 'visible' || current !== DEPOSIT_OUTCOME.confirming || isLookingAgain) return;

      isLookingAgain = true;
      await poll(DEPOSIT_POLL.totalMs);
      isLookingAgain = false;
    };

    document.addEventListener('visibilitychange', onVisible);

    if (kind === 'cancel') cancel();
    else poll(0);

    return () => {
      isActive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [kind, token, portalId]);

  return outcome;
};

export default useDepositOutcome;
